import _ from "lodash";
/**--external-- */
import React from "react";
import PropTypes from "prop-types";

/**--internal-- */
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  CircleOutlined,
  DeleteOutlined,
  DownArrowOutlined,
  TickCircleFilled,
  UpArrowOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";

/**--relative-- */
import classes from "./ElementContextMenu.scss";
import { isDropzone } from "../FindHotspot/FindHotspotUtils";

// * Menu separator
const Separator = () => <div className={classes.contextMenuButtonSeparator} />;

/**
 *
 * @param { node, onClick  }
 * @returns Button to remove element from canvas
 */
const RemoveItemButton = ({ node, onClick = () => {} }) => {
  const handleRemoveItem = () => {
    const id = node.id();
    onClick(id);
  };

  return (
    <IconButton
      dsVersion="2.0"
      type="plain"
      variant="neutral"
      _legacy_variant="plain"
      onClick={handleRemoveItem}
      icon={<DeleteOutlined size="xx-small" variant="critical" />}
    ></IconButton>
  );
};

/**
 *
 * @param { node, onClick  }
 * @returns Button to order elements on canvas category wise
 */
const OrderItemButtons = ({ t, node, onOrderElement = () => {} }) => {
  const handleOrderElement = type => {
    const id = node.id();
    onOrderElement(id, type);
  };

  return (
    <>
      <Tooltip tooltip={t("workbook:bring_to_front")}>
        <IconButton
          dsVersion="2.0"
          type="plain"
          variant="neutral"
          _legacy_variant="plain"
          onClick={() => handleOrderElement("UP")}
          icon={<UpArrowOutlined size="xx-small" variant="subtle" />}
        ></IconButton>
      </Tooltip>
      <Tooltip tooltip={t("workbook:send_to_back")}>
        <IconButton
          dsVersion="2.0"
          type="plain"
          variant="neutral"
          _legacy_variant="plain"
          onClick={() => handleOrderElement("DOWN")}
          icon={<DownArrowOutlined size="xx-small" variant="subtle" />}
        ></IconButton>
      </Tooltip>
    </>
  );
};

/**
 *
 * @param { node, onClick }
 * @returns Button to convert `draggable <> decorative` converter
 */
const CorrectHotspotToggleButton = ({
  t,
  node,
  findHotspotAnswers,
  onClick,
}) => {
  const id = node.id();
  const isCorrect = _.includes(findHotspotAnswers, id);

  const updateToggleState = () => {
    const updatedFindHotspotAnswers = isCorrect
      ? _.filter(findHotspotAnswers, _id => id !== _id)
      : [id, ...findHotspotAnswers];

    onClick(updatedFindHotspotAnswers);
  };

  return (
    <Button
      onClick={updateToggleState}
      dsVersion="2.0"
      variant="neutral"
      type="plain"
      _legacy_variant="plain"
      icon={
        isCorrect ? (
          <TickCircleFilled size="x-small" variant="success" />
        ) : (
          <CircleOutlined size="x-small" variant="subtle" />
        )
      }
    >
      {t("worksheet:correct")}
    </Button>
  );
};

/**
 *
 *
 *
 *
 * @param {*} props
 * @returns ElementContextMenu
 */
const ElementContextMenu = (props = {}) => {
  const { t } = useI18n();

  const {
    node,
    findHotspotAnswers = [],
    onRemove = () => {},
    onToggleAnswer = () => {},
    onOrderElement = () => {},
  } = props;
  if (!node) return null;

  const category = _.first(_.split(node.name(), "#"));
  const isHotspot = isDropzone(category);

  return (
    <>
      {isHotspot && (
        <>
          <CorrectHotspotToggleButton
            t={t}
            node={node}
            onClick={onToggleAnswer}
            findHotspotAnswers={findHotspotAnswers}
          />
          <Separator />
        </>
      )}
      <OrderItemButtons t={t} node={node} onOrderElement={onOrderElement} />
      <Separator />
      <RemoveItemButton node={node} onClick={onRemove} />
    </>
  );
};

export default ElementContextMenu;

ElementContextMenu.defaultProps = {
  node: {},
  onRemove: () => {},
  onChange: () => {},
  onOrderElement: () => {},
};

ElementContextMenu.propTypes = {
  node: PropTypes.any,
  onRemove: PropTypes.func,
  onChange: PropTypes.func,
  onOrderElement: PropTypes.func,
};
